import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utilities/ui'

const buttonVariants = cva(
  "font-outfit text-button font-medium inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border border-transparent text-dark-800 transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: 'bg-turquoise shadow-sm hover:bg-turquoise/90',
        outline:
          'border border-black bg-transparent hover:bg-black/5 focus-visible:ring-black/20',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'px-4 py-2.5',
        sm: 'px-3 py-1.5 text-sm',
        lg: 'px-6 py-2',
        icon: 'size-10',
        clear: 'h-auto p-0',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        className: "[&_svg:not([class*='size-'])]:size-3.5",
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: React.ReactNode
  iconPosition?: 'leading' | 'trailing'
}

const ButtonIcon: React.FC<React.ComponentProps<'span'>> = ({ className, ...props }) => (
  <span
    aria-hidden
    className={cn('inline-flex shrink-0 items-center', className)}
    data-slot="button-icon"
    {...props}
  />
)

function Button({
  asChild = false,
  children,
  className,
  icon,
  iconPosition = 'trailing',
  size,
  variant,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  const content = (() => {
    if (!icon) return children

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{ children?: React.ReactNode }>

      return React.cloneElement(child, {
        children: (
          <>
            {iconPosition === 'leading' ? <ButtonIcon>{icon}</ButtonIcon> : null}
            {child.props.children}
            {iconPosition === 'trailing' ? <ButtonIcon>{icon}</ButtonIcon> : null}
          </>
        ),
      })
    }

    return (
      <>
        {iconPosition === 'leading' ? <ButtonIcon>{icon}</ButtonIcon> : null}
        {children}
        {iconPosition === 'trailing' ? <ButtonIcon>{icon}</ButtonIcon> : null}
      </>
    )
  })()

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {content}
    </Comp>
  )
}

export { Button, ButtonIcon, buttonVariants }
